/**
 * EditInvoiceDateDialog (UAT-003 — 2026-02-12)
 *
 * Primary-Admin-only narrow correction of `invoice_date`. Every other
 * invoice field (line items, totals, GST, customer, PO link,
 * `invoice_number`, `created_at`) is untouched. `due_date` recomputes
 * atomically from the new `invoice_date` + the invoice's existing
 * `payment_terms`.
 *
 * Backend:
 *   GET  /api/billing/invoice/{id}/edit-date-impact?new_date=YYYY-MM-DD
 *   PATCH /api/billing/invoice/{id}/edit-date
 *     body: { new_invoice_date, reason, note }
 *
 * Gates (server-authoritative — UI surfaces them via the impact preview):
 *   G-1  GST-Filed parent invoice    (non-overridable)
 *   G-2  Cancelled invoice            (non-overridable)
 *   G-3  Temporal invariant
 *        invoice_date <= min(payment_date)   (non-overridable, even under MM)
 *   G-4  Cross-FY edit                (requires Migration Mode ACTIVE)
 *   G-5  TDS Claimed/Adjusted payment (non-overridable)
 *
 * Downstream impact (informational notice in the dialog):
 *   - GST periodisation (revenue period bucketed by invoice_date)
 *   - Aging Reports
 *   - Collections priority
 *   - Cashflow forecast
 */
import { useEffect, useState, useRef } from "react";
import {
    CalendarBlank, ShieldCheck, ArrowRight, Info, WarningCircle, CheckCircle, Database,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const REASONS = [
    { value: "TypoCorrection",            label: "Typo correction — wrong date typed" },
    { value: "HistoricalDateCorrection",  label: "Historical date correction (migration / legacy data)" },
    { value: "Other",                     label: "Other (substantively explain in the note)" },
];

const NOTE_MIN = 5;

function isoToday(dateLike) {
    if (!dateLike) return "";
    const s = typeof dateLike === "string" ? dateLike : "";
    return s.slice(0, 10);
}

function indianFy(iso) {
    if (!iso || iso.length < 7) return "";
    const y = parseInt(iso.slice(0, 4), 10);
    const m = parseInt(iso.slice(5, 7), 10);
    if (Number.isNaN(y) || Number.isNaN(m)) return "";
    const start = m >= 4 ? y : y - 1;
    return `${start}-${String(start + 1).slice(-2)}`;
}

export default function EditInvoiceDateDialog({ open, onOpenChange, invoice, onDone }) {
    const [newDate, setNewDate] = useState("");
    const [reason, setReason] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [ack, setAck] = useState(false);
    const [impact, setImpact] = useState(null);
    const [impactBusy, setImpactBusy] = useState(false);
    const impactTimer = useRef(null);

    useEffect(() => {
        // Wrap state-sets in a microtask to satisfy
        // `react-hooks/set-state-in-effect` under React 19.
        Promise.resolve().then(() => {
            if (!open) {
                setNewDate(""); setReason(""); setNote(""); setAck(false);
                setBusy(false); setImpact(null); setImpactBusy(false);
                return;
            }
            setNewDate(isoToday(invoice?.invoice_date));
            setReason("");
            setNote("");
            setAck(false);
            setImpact(null);
        });
    }, [open, invoice?.id, invoice?.invoice_date]);

    const oldIso = isoToday(invoice?.invoice_date);
    const oldFy = indianFy(oldIso);
    const newFy = indianFy(newDate);
    const willCrossFy = oldFy && newFy && oldFy !== newFy;
    const isNoop = oldIso && newDate && oldIso === newDate;

    // Debounced impact preview — runs whenever newDate changes and is valid.
    useEffect(() => {
        if (!open || !invoice?.id) return;
        if (impactTimer.current) clearTimeout(impactTimer.current);
        if (!newDate || newDate.length < 10) {
            Promise.resolve().then(() => setImpact(null));
            return;
        }
        // Defer setTimeout scheduling into a microtask so the lint rule
        // (react-hooks/set-state-in-effect, React 19) is satisfied — the
        // setState calls then happen outside the effect's synchronous body.
        Promise.resolve().then(() => {
            impactTimer.current = setTimeout(async () => {
                setImpactBusy(true);
                try {
                    const { data } = await api.get(
                        `/billing/invoice/${invoice.id}/edit-date-impact`,
                        { params: { new_date: newDate } },
                    );
                    setImpact(data);
                } catch {
                    setImpact(null);
                    // Silent — surface only on submit
                } finally {
                    setImpactBusy(false);
                }
            }, 250);
        });
        return () => { if (impactTimer.current) clearTimeout(impactTimer.current); };
    }, [open, invoice?.id, newDate]);

    const blocks = impact?.blocks || [];
    const wouldSucceed = impact?.would_succeed === true;
    const valid =
        !!newDate && !!reason && note.trim().length >= NOTE_MIN && ack &&
        (impact ? wouldSucceed : true);  // if preview hasn't loaded yet, allow trying
    const isDirty = !!reason || note.length > 0 || newDate !== oldIso;

    const submit = async () => {
        if (!valid) return;
        setBusy(true);
        try {
            const body = {
                new_invoice_date: newDate,
                reason,
                note: note.trim(),
            };
            await api.patch(`/billing/invoice/${invoice.id}/edit-date`, body);
            toast.success(`Invoice date updated to ${formatDate(newDate)}`);
            onDone?.();
            onOpenChange?.(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to update invoice date"));
        } finally {
            setBusy(false);
        }
    };

    if (!invoice) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-xl"
            testId="edit-invoice-date-dialog"
            srTitle="Edit invoice date"
            srDescription="Primary-Admin-only date-only correction of an invoice. Line items, totals, GST, customer, PO link, invoice_number and created_at are NOT touched."
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <CalendarBlank size={18} weight="duotone" className="text-amber-600" />
                        Edit invoice date
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200">
                            <ShieldCheck size={9} weight="fill" /> Primary Admin
                        </span>
                    </div>
                    <div className="text-xs text-slate-500">
                        Date-only correction — line items, totals, GST, customer, PO link,
                        invoice number and creation timestamp stay intact. Old date is
                        pushed onto the invoice&apos;s date-history audit trail.
                    </div>
                </div>
            }
        >
            <div className="space-y-4 py-2">
                {/* Current row context */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-700 space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="reedos-eyebrow text-[10px]">Current invoice</span>
                        <span className="text-[10px] text-slate-400 font-mono">{invoice.invoice_number}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span>Date <b className="text-slate-900">{formatDate(invoice.invoice_date)}</b></span>
                        <span>·</span>
                        <span>Due <b className="text-slate-900">{formatDate(invoice.due_date)}</b></span>
                        <span>·</span>
                        <span>FY <b className="text-slate-900">{oldFy || "—"}</b></span>
                        <span>·</span>
                        <span>Terms <b className="text-slate-900">{invoice.payment_terms || "—"}</b></span>
                    </div>
                </div>

                {/* New date input */}
                <div>
                    <Label className="text-xs font-semibold">New invoice date *</Label>
                    <Input
                        type="date"
                        value={newDate}
                        onChange={(e) => setNewDate(e.target.value)}
                        className="mt-1"
                        data-testid="edit-invoice-date-input"
                    />
                    {newDate && (
                        <div className="text-[11px] text-slate-600 mt-1.5 flex items-center gap-2 flex-wrap">
                            <span>FY {newFy || "—"}</span>
                            {oldFy && newFy && (
                                <>
                                    <ArrowRight size={10} className="text-slate-400" />
                                    <span className={willCrossFy ? "text-amber-700 font-semibold" : "text-slate-500"}>
                                        {willCrossFy ? "Cross-FY change" : "Same FY"}
                                    </span>
                                </>
                            )}
                            {isNoop && (
                                <span className="text-slate-500" data-testid="edit-invoice-date-noop-info">
                                    Same as current — pick a different date to proceed.
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Impact preview panel */}
                {newDate && !isNoop && (
                    <div
                        className="rounded-md border bg-white px-3 py-2.5 text-xs space-y-2"
                        data-testid="edit-invoice-date-impact-panel"
                    >
                        <div className="flex items-center justify-between">
                            <span className="reedos-eyebrow text-[10px] flex items-center gap-1">
                                <Info size={11} weight="fill" className="text-indigo-600" />
                                Impact preview
                            </span>
                            {impactBusy && <span className="text-[10px] text-slate-400">computing…</span>}
                        </div>
                        {impact && (
                            <>
                                <div className="grid grid-cols-2 gap-2 text-[11px]">
                                    <div>
                                        <span className="text-slate-500">New invoice date:</span>{" "}
                                        <b className="text-slate-900">{formatDate(impact.new_invoice_date)}</b>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">New due date:</span>{" "}
                                        <b className="text-slate-900">{formatDate(impact.new_due_date)}</b>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">FY shift:</span>{" "}
                                        <b className="text-slate-900">{impact.from_fy || "—"} → {impact.to_fy || "—"}</b>
                                    </div>
                                    <div>
                                        <span className="text-slate-500">Migration Mode:</span>{" "}
                                        {impact.migration_mode_active ? (
                                            <b className="text-rose-700">ACTIVE</b>
                                        ) : (
                                            <span className="text-slate-700">not active</span>
                                        )}
                                    </div>
                                </div>

                                {/* Migration correction tagging notice */}
                                {impact.migration_mode_active && willCrossFy && wouldSucceed && (
                                    <div
                                        className="rounded border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800 flex items-start gap-1.5"
                                        data-testid="edit-invoice-date-mm-tag-notice"
                                    >
                                        <Database size={11} weight="fill" className="mt-0.5 shrink-0" />
                                        <span>
                                            Cross-FY edit will be permanently tagged as a{" "}
                                            <b>MIGRATION CORRECTION</b> in the audit trail and on the
                                            invoice header. This tag persists forever — even after
                                            Migration Mode sign-off.
                                        </span>
                                    </div>
                                )}

                                {/* Blocks */}
                                {blocks.length > 0 && (
                                    <div className="space-y-1" data-testid="edit-invoice-date-blocks">
                                        {blocks.map((b, i) => (
                                            <div
                                                key={i}
                                                className="rounded border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800 flex items-start gap-1.5"
                                                data-testid={`edit-invoice-date-block-${b.code}`}
                                            >
                                                <WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                                                <span>{b.detail}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Warnings (informational only) */}
                                {(impact.warnings || []).map((w, i) => (
                                    <div
                                        key={i}
                                        className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 flex items-start gap-1.5"
                                        data-testid={`edit-invoice-date-warning-${w.code}`}
                                    >
                                        <Info size={11} weight="fill" className="mt-0.5 shrink-0" />
                                        <span>{w.detail}</span>
                                    </div>
                                ))}

                                {/* Success preview */}
                                {wouldSucceed && (
                                    <div
                                        className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-800 flex items-start gap-1.5"
                                        data-testid="edit-invoice-date-would-succeed"
                                    >
                                        <CheckCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                                        <span>
                                            All gates pass. Invoice number, created_at and line items
                                            will stay intact; only invoice_date + due_date change.
                                        </span>
                                    </div>
                                )}

                                {/* Downstream impact notice (always shown when valid) */}
                                {(impact.downstream_impact_notice || []).length > 0 && (
                                    <div className="rounded border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-[11px] text-indigo-900">
                                        <div className="font-semibold mb-1 flex items-center gap-1">
                                            <Info size={11} weight="fill" /> Downstream surfaces affected:
                                        </div>
                                        <ul className="list-disc ml-4 space-y-0.5">
                                            {impact.downstream_impact_notice.map((n, i) => (
                                                <li key={i}>{n}</li>
                                            ))}
                                        </ul>
                                        <div className="text-[10px] text-indigo-700 mt-1 italic">
                                            AMC billing_start_date and Credit Note dates do NOT cascade —
                                            adjust those separately if required.
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {!impact && !impactBusy && (
                            <div className="text-[11px] text-slate-400">
                                Pick a date above to preview the impact.
                            </div>
                        )}
                    </div>
                )}

                {/* Reason */}
                <div>
                    <Label className="text-xs font-semibold">Correction reason *</Label>
                    <select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        data-testid="edit-invoice-date-reason-select"
                    >
                        <option value="">— Pick a reason —</option>
                        {REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                </div>

                {/* Note */}
                <div>
                    <Label className="text-xs font-semibold">Operator note *</Label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What was wrongly entered, supporting source (e.g. Tally voucher, customer PO date)…"
                        rows={3}
                        maxLength={1000}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        data-testid="edit-invoice-date-note-input"
                    />
                    <div className="text-[10px] text-slate-400 mt-1">
                        {note.length} / 1000 · min {NOTE_MIN} characters
                    </div>
                </div>

                {/* Acknowledgement */}
                <label
                    className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 cursor-pointer select-none"
                    data-testid="edit-invoice-date-ack-row"
                >
                    <input
                        type="checkbox"
                        checked={ack}
                        onChange={(e) => setAck(e.target.checked)}
                        className="mt-0.5"
                        data-testid="edit-invoice-date-ack-checkbox"
                    />
                    <span className="text-[11px] text-slate-700 leading-snug">
                        I have reviewed the impact above. I understand the invoice number,
                        created_at timestamp and line items stay intact; only invoice_date
                        and due_date will change. AMC and Credit Note dates do NOT cascade
                        and must be managed separately.
                    </span>
                </label>
            </div>
            <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                <FormDialogCancel disabled={busy} testId="edit-invoice-date-cancel-btn" />
                <Button
                    onClick={submit}
                    disabled={busy || !valid}
                    className="bg-rose-600 hover:bg-rose-500 text-white gap-1.5"
                    data-testid="edit-invoice-date-confirm-btn"
                >
                    <CalendarBlank size={14} />
                    {busy ? "Saving…" : "Save corrected date"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
